import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { QuoteList } from '../components/QuoteList'
import { QuoteCard } from '../components/QuoteCard'
import { Loading } from '../components/Loading'
import { useInfiniteQuotes } from '../hooks/useQuotes'
import { useTags } from '../hooks/useTags'
import { useFTSSearch, useAISearch, useAIKeyStatus } from '../hooks/useSearch'

type SearchMode = 'tags' | 'fts' | 'ai'

interface AxiosError extends Error {
  response?: { data?: { detail?: string } }
}

const renderSnippet = (html: string) => {
  const raw = html.split(/<\/?mark>/g)
  return raw.map((part, i) =>
    i % 2 === 1
      ? <mark key={i} style={{ background: '#fff3b0', borderRadius: '2px', padding: '0 2px' }}>{part}</mark>
      : <span key={i}>{part}</span>
  )
}

export const Home = () => {
  const [mode, setMode] = useState<SearchMode>('tags')
  const [tagSearch, setTagSearch] = useState('')
  const [ftsQuery, setFtsQuery] = useState('')
  const [ftsInput, setFtsInput] = useState('')
  const [aiQuery, setAiQuery] = useState('')
  const [aiInput, setAiInput] = useState('')
  const [aiTriggered, setAiTriggered] = useState(false)
  const [selectedTagId, setSelectedTagId] = useState<number | undefined>(undefined)
  const sentinelRef = useRef<HTMLDivElement>(null)

  const { data: tagData, isLoading: tagsLoading } = useTags()
  const { data: quotesData, isLoading: quotesLoading, error: quotesError, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuotes(20, selectedTagId)
  const { data: ftsData, isLoading: ftsLoading, error: ftsError } = useFTSSearch(ftsQuery)
  const { data: aiData, isLoading: aiLoading, error: aiError } = useAISearch(aiQuery, aiTriggered)
  const { data: aiKeyStatus } = useAIKeyStatus()

  const quotes = quotesData?.pages.flatMap((page) => page.items) ?? []

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      { threshold: 0.1 }
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const filteredTags = tagData?.filter((t) =>
    t.name.toLowerCase().includes(tagSearch.toLowerCase())
  )

  const tabStyle = (active: boolean): React.CSSProperties => ({
    padding: '0.35rem 0.9rem',
    borderRadius: '6px 6px 0 0',
    border: '1px solid #ccc',
    borderBottom: active ? '1px solid #fff' : '1px solid #ccc',
    background: active ? '#fff' : '#f5f5f5',
    cursor: 'pointer',
    fontWeight: active ? 600 : 400,
    fontSize: '0.9rem',
    marginBottom: '-1px',
    position: 'relative',
    zIndex: active ? 1 : 0,
  })

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <h2 style={{ margin: 0 }}>
          {mode === 'tags' && selectedTagId
            ? <>Zitate mit Tag: <Link to={`/tags/${selectedTagId}`} className="tag">#{tagData?.find(t => t.id === selectedTagId)?.name}</Link></>
            : mode === 'fts' && ftsQuery
            ? <>Volltext: <em style={{ fontWeight: 400 }}>{ftsQuery}</em></>
            : mode === 'ai' && aiQuery && aiTriggered
            ? <>KI-Suche: <em style={{ fontWeight: 400 }}>{aiQuery}</em></>
            : 'Alle Zitate'}
        </h2>
        {mode === 'tags' && selectedTagId && (
          <button onClick={() => { setSelectedTagId(undefined); setTagSearch('') }} style={{ fontSize: '0.85rem' }}>
            Filter aufheben
          </button>
        )}
      </div>

      {/* Search panel */}
      <div style={{ marginBottom: '1.5rem', border: '1px solid #ccc', borderRadius: '0 6px 6px 6px' }}>
        {/* Tabs */}
        <div style={{ display: 'flex', gap: '0.25rem', padding: '0.5rem 0.75rem 0', background: '#f9f9f9', borderBottom: '1px solid #ccc' }}>
          <button style={tabStyle(mode === 'tags')} onClick={() => setMode('tags')}>Tags</button>
          <button style={tabStyle(mode === 'fts')} onClick={() => setMode('fts')}>Volltext</button>
          <button style={tabStyle(mode === 'ai')} onClick={() => setMode('ai')}>KI-Suche</button>
        </div>

        {/* Tab content */}
        <div style={{ padding: '0.75rem 1rem' }}>

          {mode === 'tags' && (
            <>
              <input
                type="text"
                placeholder="Tag suchen…"
                value={tagSearch}
                onChange={(e) => setTagSearch(e.target.value)}
                style={{ padding: '0.4rem 0.75rem', borderRadius: '6px', border: '1px solid #ccc', width: '220px' }}
              />
              {tagSearch && filteredTags && filteredTags.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
                  {filteredTags.map((tag) => (
                    <button
                      key={tag.id}
                      className="tag"
                      style={{ cursor: 'pointer', border: 'none', background: selectedTagId === tag.id ? '#555' : undefined }}
                      onClick={() => { setSelectedTagId(tag.id); setTagSearch('') }}
                    >
                      #{tag.name}
                    </button>
                  ))}
                </div>
              )}
              {tagSearch && filteredTags?.length === 0 && (
                <p style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#888' }}>Kein Tag gefunden.</p>
              )}
            </>
          )}

          {mode === 'fts' && (
            <form
              onSubmit={(e) => { e.preventDefault(); setFtsQuery(ftsInput.trim()) }}
              style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}
            >
              <input
                type="text"
                placeholder="Suchbegriff im Zitat-Text…"
                value={ftsInput}
                onChange={(e) => setFtsInput(e.target.value)}
                style={{ padding: '0.4rem 0.75rem', borderRadius: '6px', border: '1px solid #ccc', width: '280px' }}
              />
              <button type="submit" className="login-button" style={{ padding: '0.4rem 1rem' }}>Suchen</button>
              {ftsQuery && (
                <button type="button" onClick={() => { setFtsQuery(''); setFtsInput('') }} style={{ fontSize: '0.85rem' }}>
                  Zurücksetzen
                </button>
              )}
            </form>
          )}

          {mode === 'ai' && (
            <>
              {!aiKeyStatus?.configured && (
                <p style={{ fontSize: '0.85rem', color: '#888', marginBottom: '0.5rem' }}>
                  Kein API-Key für die KI-Suche konfiguriert.{' '}
                  <Link to="/settings">In den Einstellungen hinterlegen.</Link>
                </p>
              )}
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  const q = aiInput.trim()
                  if (!q) return
                  setAiQuery(q)
                  setAiTriggered(true)
                }}
                style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}
              >
                <input
                  type="text"
                  placeholder='Thema beschreiben, z. B. "Erkenntnistheorie"...'
                  value={aiInput}
                  onChange={(e) => setAiInput(e.target.value)}
                  disabled={!aiKeyStatus?.configured}
                  style={{ padding: '0.4rem 0.75rem', borderRadius: '6px', border: '1px solid #ccc', width: '320px',
                           opacity: aiKeyStatus?.configured ? 1 : 0.5 }}
                />
                <button
                  type="submit"
                  className="login-button"
                  disabled={!aiKeyStatus?.configured}
                  style={{ padding: '0.4rem 1rem' }}
                >
                  Suchen
                </button>
                {aiTriggered && (
                  <button type="button" onClick={() => { setAiQuery(''); setAiInput(''); setAiTriggered(false) }} style={{ fontSize: '0.85rem' }}>
                    Zurücksetzen
                  </button>
                )}
              </form>
            </>
          )}

        </div>
      </div>

      {/* Results */}
      {mode === 'tags' && (
        <>
          <QuoteList quotes={quotes} isLoading={quotesLoading || tagsLoading} error={quotesError} isFetchingNextPage={isFetchingNextPage} />
          <div ref={sentinelRef} style={{ height: '1px' }} />
        </>
      )}

      {mode === 'fts' && !ftsQuery && (
        <p style={{ color: '#888' }}>Suchbegriff eingeben und auf „Suchen" klicken.</p>
      )}

      {mode === 'fts' && ftsQuery && (
        ftsLoading ? <Loading /> :
        ftsError ? <div className="error-message">Fehler: {(ftsError as Error).message}</div> :
        !ftsData?.items?.length ? (
          <div className="empty-state"><h3>Keine Treffer</h3><p>Kein Zitat enthält „{ftsQuery}".</p></div>
        ) : (
          <>
            <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.75rem' }}>
              {ftsData.total} Treffer
            </p>
            <div className="quote-grid">
              {ftsData.items.map((quote) => (
                <div key={quote.id}>
                  <QuoteCard quote={quote} />
                  {quote.snippet && (
                    <p style={{ fontSize: '0.8rem', color: '#555', margin: '0.25rem 0 0.75rem',
                                padding: '0.3rem 0.6rem', background: '#fafafa', borderLeft: '3px solid #ddd',
                                borderRadius: '0 4px 4px 0' }}>
                      {renderSnippet(quote.snippet)}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </>
        )
      )}

      {mode === 'ai' && !aiTriggered && (
        <p style={{ color: '#888' }}>Thema eingeben – die KI sucht passende Zitate für dich.</p>
      )}

      {mode === 'ai' && aiTriggered && (
        aiLoading ? (
          <div>
            <Loading />
            <p style={{ textAlign: 'center', color: '#888', fontSize: '0.9rem' }}>KI analysiert deine Sammlung…</p>
          </div>
        ) :
        aiError ? (
          <div className="error-message">
            {(aiError as AxiosError)?.response?.data?.detail ?? (aiError as Error).message}
          </div>
        ) :
        !aiData?.items?.length ? (
          <div className="empty-state"><h3>Keine Treffer</h3><p>Die KI hat keine passenden Zitate zu „{aiQuery}" gefunden.</p></div>
        ) : (
          <>
            <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.75rem' }}>
              {aiData.items.length} Treffer
            </p>
            <div className="quote-grid">
              {aiData.items.map((quote) => (
                <div key={quote.id}>
                  <QuoteCard quote={quote} />
                  {aiData.explanations[String(quote.id)] && (
                    <p style={{ fontSize: '0.8rem', color: '#555', margin: '0.25rem 0 0.75rem',
                                padding: '0.3rem 0.6rem', background: '#f0f7ff', borderLeft: '3px solid #90bef0',
                                borderRadius: '0 4px 4px 0' }}>
                      {aiData.explanations[String(quote.id)]}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </>
        )
      )}
    </div>
  )
}
